import React, { useCallback, useId } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { textColorMessages as messages } from '@atlaskit/editor-common/messages/text-color';
import { getInputMethodFromParentKeys } from '@atlaskit/editor-common/toolbar';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar/context';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { textPaletteTooltipMessages } from '@atlaskit/editor-common/ui-color/ColorPalette/paletteMessagesTokenModeNames';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { hexToEditorTextPaletteColor } from '@atlaskit/editor-palette/text';
import type { ToolbarComponentTypes } from '@atlaskit/editor-toolbar-model/types';
import { ColorPalette } from '@atlaskit/editor-toolbar/color-palette';
import { useToolbarDropdownMenu } from '@atlaskit/editor-toolbar/toolbar-dropdown-menu-context';
import Heading from '@atlaskit/heading/heading';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Bleed } from '@atlaskit/primitives/compiled/bleed';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import type { TextColorPlugin } from '../textColorPluginType';

const TEXT_COLOR_PICKER_COLUMNS = 10;

const styles = cssMap({
	container: {
		gap: token('space.075'),
	},
});

interface TextColorMenuItemProps {
	api: ExtractInjectionAPI<TextColorPlugin> | undefined;
	parents: ToolbarComponentTypes;
}

export function TextColorMenuItem({ api, parents }: TextColorMenuItemProps): React.JSX.Element {
	const { color, defaultColor, palette } = useSharedPluginStateWithSelector(
		api,
		['textColor'],
		(states) => ({
			color: states.textColorState?.color,
			defaultColor: states.textColorState?.defaultColor || null,
			palette: states.textColorState?.palette || [],
		}),
	);
	const { editorView } = useEditorToolbar();
	const context = useToolbarDropdownMenu();
	const closeMenu = context?.closeMenu;

	const { formatMessage } = useIntl();
	const labelId = useId();
	const isNewColorPaletteEnabled = expValEquals(
		'platform_editor_lovability_text_bg_color',
		'isEnabled',
		true,
	);

	const handleTextColorChange = useCallback(
		(color: string, event: React.MouseEvent | React.KeyboardEvent) => {
			if (!editorView?.state || !editorView?.dispatch) {
				return;
			}
			if (api?.textColor?.actions?.changeColor) {
				api.textColor.actions.changeColor(color, getInputMethodFromParentKeys(parents))(
					editorView.state,
					editorView.dispatch,
				);

				if (!isNewColorPaletteEnabled) {
					closeMenu?.(event);
				}
			}
		},
		[
			editorView?.state,
			editorView?.dispatch,
			api?.textColor.actions,
			parents,
			closeMenu,
			isNewColorPaletteEnabled,
		],
	);

	const colorPalette = (
		<ColorPalette
			ariaLabelledBy={labelId}
			cols={isNewColorPaletteEnabled ? TEXT_COLOR_PICKER_COLUMNS : undefined}
			gap={isNewColorPaletteEnabled ? 'space.0' : undefined}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onClick={(color, _, event) => {
				handleTextColorChange(color, event);
			}}
			selectedColor={color || defaultColor}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			paletteOptions={{
				palette: palette,
				hexToPaletteColor: hexToEditorTextPaletteColor,
				paletteColorTooltipMessages: textPaletteTooltipMessages,
			}}
		/>
	);

	return (
		<Stack xcss={styles.container} testId="text-color-menu-item">
			<Heading
				id={labelId}
				size="xxsmall"
				// The label names the palette's radio group; a heading inside a menu is skipped and has no stable level here
				as={fg('platform_editor_a11y_color_palette_radiogroup') ? 'div' : undefined}
			>
				{formatMessage(messages.textColorTooltip)}
			</Heading>
			{isNewColorPaletteEnabled ? <Bleed inline="space.025">{colorPalette}</Bleed> : colorPalette}
		</Stack>
	);
}
